import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { bookingManagementApi } from "./booking-management-api";
import bookingPaceSlice from "./bookingPaceSlice";
import userSettingsReducer from "./slices/userSettingsSlice";
import apartmentFiltersReducer from "./slices/apartmentFiltersSlice";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import occupancySlice from "./occupancySlice";
import { strApi } from "@/store/str-api";
import { clientErrorReporter } from "./client-error-reporter";

export const store = configureStore({
    reducer: {
        [bookingManagementApi.reducerPath]: bookingManagementApi.reducer,
        [strApi.reducerPath]: strApi.reducer,
        bookingPace: bookingPaceSlice,
        occupancy: occupancySlice.reducer,
        userSettings: userSettingsReducer,
        apartmentFilters: apartmentFiltersReducer,
    },
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false,
            // The immutable-state invariant middleware is a DEV-ONLY check (RTK
            // strips it from production builds) that deep-scans the entire state
            // tree on every dispatch. Under CI CPU load that scan can exceed its
            // 32ms timing threshold and emit a `console.warn` advisory
            // ("ImmutableStateInvariantMiddleware took 44ms…") — which the test
            // console gate turns into a hard failure, flaking the suite
            // nondeterministically. Disable it under Vitest so the middleware (and
            // thus the advisory) is never installed during tests; dev keeps the
            // mutation check, prod already omits it.
            // eslint-disable-next-line no-undef -- Vite statically replaces process.env.NODE_ENV at build; typed via react-scripts
            ...(process.env.NODE_ENV === "test" ? { immutableCheck: false } : {}),
        }).concat(bookingManagementApi.middleware, strApi.middleware, clientErrorReporter),
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

const useAppDispatch = () => useDispatch<AppDispatch>();

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export { useAppDispatch };
